import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as InfoIcon } from '../../icons/Icon-Info-Left-Line.svg';
import useIconUrl from '../../utils/hooks/useIconUrl';
import useLocalize from '../../utils/hooks/useLocalize';
import { CANDIDATE_COUNT } from '../../utils/scanCandidates';
import AddToMarketBoardModal from '../marketBoard/AddToMarketBoardModal';
import { renderCountChunk } from './aiScannerChunks';
import StockCard from './StockCard';

// `symbol.industry.9999` exists in both string tables, mapped to an en-dash —
// it is the table's placeholder for "industry unknown", not a 34th industry.
const UNKNOWN_INDUSTRY_CODE = '9999';

/**
 * STORY-013: the results step, appended to the conversation once both
 * investment questions are answered — confirmation, count, AI summary, candidate
 * cards, favorites button and disclaimer. Each card's logo is the Wharf icon
 * built here from the current token (STORY-011).
 *
 * #736: the live candidates arrive ALREADY RANKED and resolved. The ranking
 * runs once per round in `AiScannerContainer` (`selectCandidates`) and is
 * passed down, rather than being recomputed here — the same candidates go to
 * the summarizer and onto these cards, so the summary can never describe a
 * different five than the member is looking at.
 *
 * That also converges the live and restored paths onto one shape: a candidate
 * is `{ symbol, companyName }` either way, so the card body no longer branches
 * on where the name came from. STORY-024 (#734) adds `jpIndustry` to that
 * shape for the same reason — the seam owns every `state.symbols.data` read.
 *
 * @param {Object} props
 * @param {Array<{symbol: string, companyName: string, jpIndustry: string}>}
 *   [props.candidates] - Live ranked candidates (ignored when
 *   `overrideResults` is provided).
 * @param {Array<{symbol: string, companyName: string, shortDescription: string, jpIndustry: ?string}>}
 *   [props.overrideResults] - STORY (#725): a persisted `SearchRound.results`
 *   array. When provided, the card list is built directly from it —
 *   `companyName`/`shortDescription`/`jpIndustry` used as-is, with no
 *   `state.symbols.data` lookup — so restoring never re-fetches or recomputes
 *   anything (FR-009).
 * @param {string} [props.overrideSummaryText] - STORY (#725): a persisted
 *   round's `aiSummary`, rendered in place of `state.scans.aiSummary.text`.
 * @param {string[]} [props.initialSelected] - Symbols ticked when the step
 *   mounts, so a result the member already ticked comes back as they left it.
 *   Read once; the step owns its ticks from then on. Not filtered against the
 *   cards: a tick for a symbol no longer shown is ignored by `activeSelection`,
 *   the same rule the live step uses.
 * @param {Function} [props.onSelectionChange] - Called with every ticked
 *   symbol after each toggle, so the owner can save them.
 * @returns {JSX.Element}
 */
export const ScanResults = ({
  candidates: liveCandidates = [],
  overrideResults,
  overrideSummaryText,
  initialSelected,
  onSelectionChange
}) => {
  const localize = useLocalize();
  const iconUrl = useIconUrl();
  const [selected, setSelected] = useState(() => new Set(initialSelected));
  const [isBoardModalOpen, setIsBoardModalOpen] = useState(false);

  const liveSummaryText = useSelector((state) => state.scans.aiSummary.text);

  const isRestoring = Array.isArray(overrideResults);
  const summaryText = isRestoring ? overrideSummaryText : liveSummaryText;

  // STORY (#725): capped and de-duped to CANDIDATE_COUNT here too, not just
  // at persist time (`buildSearchRound`) — a session saved by code from
  // before that cap existed can still have up to MAX_SUMMARY_SYMBOLS (5)
  // results sitting in a member's localStorage, and a duplicate symbol would
  // otherwise collide on the `key={symbolId}` below.
  const restoredResults = useMemo(() => {
    if (!isRestoring) return null;
    const seen = new Set();
    return overrideResults
      .filter((r) => {
        if (seen.has(r.symbol)) return false;
        seen.add(r.symbol);
        return true;
      })
      .slice(0, CANDIDATE_COUNT);
  }, [isRestoring, overrideResults]);

  const candidates = isRestoring ? restoredResults : liveCandidates;

  // The button reads the selection intersected with the on-screen cards, so a
  // tick left over from a no-longer-rendered card does not keep it enabled.
  const activeSelection = useMemo(
    () =>
      candidates
        .filter(({ symbol }) => selected.has(symbol))
        .map(({ symbol }) => symbol),
    [candidates, selected]
  );

  // `next` is built outside a state updater so the owner hears about each tap
  // exactly once — React may run updaters twice.
  const handleToggle = useCallback(
    (symbolId) => {
      const next = new Set(selected);
      if (!next.delete(symbolId)) next.add(symbolId);
      setSelected(next);
      if (onSelectionChange) onSelectionChange(Array.from(next));
    },
    [selected, onSelectionChange]
  );

  const handleOpenBoardModal = useCallback(() => setIsBoardModalOpen(true), []);
  // The ticks are left as they are: dismissing either modal returns the member
  // to the results screen they came from (#730), not to a cleared one.
  const handleCloseBoardModal = useCallback(
    () => setIsBoardModalOpen(false),
    []
  );

  // STORY-024 (#734) AC C20095688: the card carries the Syft industry as a tag.
  // `jpIndustry` is a numeric code, not a label — resolved through the same
  // `symbol.industry.*` table the scan list and symbol header already use.
  const industryLabel = useCallback(
    (jpIndustry) => {
      // `9999` is the table's own "unknown industry" sentinel, mapped to an
      // en-dash — `symbolOverview/PageSubtitle` DEFAULTS to it to render that
      // dash in a labelled field. A card's tag has no label beside it, so the
      // dash would stand alone and read as a broken tag. Treated as no
      // industry, which is what it means, and what the AC asks to show for it.
      if (!jpIndustry || String(jpIndustry) === UNKNOWN_INDUSTRY_CODE) return '';
      const key = `symbol.industry.${jpIndustry}`;
      const label = localize(key);
      // react-intl echoes the id back for a code that is not in the table.
      // AC: no industry value means an EMPTY slot, so a raw key must never
      // reach the card.
      return label === key ? '' : label;
    },
    [localize]
  );

  return (
    <div className="ai-scanner-results">
      <div className="ai-scanner-message">
        <p className="ai-scanner-results-confirmation">
          {localize('ai.scanner.results.confirmation')}
        </p>
        <p>
          {localize('ai.scanner.results.count', {
            count: String(candidates.length),
            b: renderCountChunk
          })}
        </p>
        {/* Bedrock is not enabled in QA yet, so an absent summary is normal. */}
        <p className="ai-scanner-results-summary">
          {summaryText || localize('ai.scanner.results.summary.placeholder')}
        </p>
        <p>{localize('ai.scanner.results.helper')}</p>
      </div>
      {/*
        STORY-024 (#734) AC C20095686: the AI disclaimer sits ABOVE the cards,
        not under the 「お気に入りボードに登録する」 button where STORY-013 first
        put it, and is rendered as the `v4 info_left-line` callout (Figma
        6064:1656) rather than as a bare paragraph. The copy itself is
        unchanged; only the placement and treatment move.
      */}
      <div className="ai-scanner-info-callout">
        <p className="ai-scanner-info-callout-heading">
          <InfoIcon
            className="ai-scanner-info-callout-icon"
            aria-hidden="true"
          />
          {localize('ai.scanner.results.disclaimer.title')}
        </p>
        <p className="ai-scanner-results-disclaimer">
          {localize('ai.scanner.results.disclaimer')}
        </p>
      </div>
      <ul className="ai-scanner-results-cards">
        {candidates.map((candidate) => (
          <StockCard
            key={candidate.symbol}
            symbolId={candidate.symbol}
            // Both paths resolve the name before they get here — the seam from
            // `state.symbols.data`, the restored round from what it persisted —
            // and both fall back to the symbol id, so no branch is needed.
            name={candidate.companyName || candidate.symbol}
            // STORY-024 (#734): the industry code travels on the candidate for
            // the same reason the name does (#736) — only the seam touches
            // `state.symbols.data`. A restored round carries its own persisted
            // code (`buildSearchRound`), so this one expression serves both
            // paths; a round saved before that field existed has none and
            // lands on the empty slot.
            industry={industryLabel(candidate.jpIndustry)}
            // Syft company description: resolved by the seam on the live path,
            // persisted on the round for a restore. `StockCard` owns the
            // placeholder for an empty one.
            description={candidate.shortDescription}
            logoUrl={iconUrl(candidate.symbol)}
            selected={selected.has(candidate.symbol)}
            onToggle={handleToggle}
          />
        ))}
      </ul>
      <button
        type="button"
        className="ai-scanner-results-register"
        disabled={activeSelection.length === 0}
        onClick={handleOpenBoardModal}
      >
        {localize('ai.scanner.results.register')}
      </button>
      {/* #730: the existing STORY-014 flow, reused as-is — the checked cards
          are handed over as one array so they register in a single write. */}
      <AddToMarketBoardModal
        open={isBoardModalOpen}
        onClose={handleCloseBoardModal}
        symbol={activeSelection}
        className="ai-scanner-add-to-board"
      />
    </div>
  );
};

ScanResults.propTypes = {
  candidates: PropTypes.arrayOf(
    PropTypes.shape({
      symbol: PropTypes.string.isRequired,
      companyName: PropTypes.string,
      // A Syft industry code (e.g. '6100'), not a label — resolved for display
      // by `industryLabel` below.
      jpIndustry: PropTypes.string,
      shortDescription: PropTypes.string
    })
  ),
  overrideResults: PropTypes.arrayOf(
    PropTypes.shape({
      symbol: PropTypes.string.isRequired,
      companyName: PropTypes.string,
      shortDescription: PropTypes.string,
      // Absent on rounds persisted before STORY-024 (#734) added it.
      jpIndustry: PropTypes.string
    })
  ),
  overrideSummaryText: PropTypes.string,
  initialSelected: PropTypes.arrayOf(PropTypes.string),
  onSelectionChange: PropTypes.func
};

export default ScanResults;
